import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "../components/site/Reveal";
import { SERVICES, INDUSTRIES, STATS } from "../data/site";

const HERO_A = "https://images.pexels.com/photos/8243094/pexels-photo-8243094.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO_B = "https://images.pexels.com/photos/6010424/pexels-photo-6010424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const THEMES = {
  A: {
    name: "Editorial White",
    bg: "#FFFFFF",
    surface: "#F6F6F4",
    text: "#0A0A0A",
    muted: "#6B6B6B",
    border: "#E4E4E1",
    accent: "#0044CC",
    hero: HERO_A,
    heroTint: "linear-gradient(90deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.2) 100%)",
  },
  B: {
    name: "Warm Institutional",
    bg: "#F4F1EA",
    surface: "#FFFFFF",
    text: "#191510",
    muted: "#6E675C",
    border: "#DED8CC",
    accent: "#1B3A5B",
    hero: HERO_B,
    heroTint: "linear-gradient(90deg, rgba(244,241,234,0.9) 0%, rgba(244,241,234,0.25) 100%)",
  },
};

const NAV = ["About", "Services", "Industries", "Government", "Careers", "Contact"];

export default function LightPreview() {
  const [params] = useSearchParams();
  const initial = params.get("v") === "B" ? "B" : "A";
  const [key, setKey] = useState(initial);
  const t = THEMES[key];

  return (
    <div style={{ background: t.bg, color: t.text }} className="min-h-screen font-sans">
      {/* Toggle bar */}
      <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
        <div style={{ background: t.text }} className="flex items-center gap-1 rounded-full p-1.5 shadow-2xl">
          {Object.keys(THEMES).map((k) => (
            <button
              key={k}
              onClick={() => setKey(k)}
              data-testid={`light-variant-${k}`}
              className="rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors"
              style={{
                background: key === k ? t.accent : "transparent",
                color: key === k ? "#fff" : t.bg,
              }}
            >
              {THEMES[k].name}
            </button>
          ))}
          <Link to="/" data-testid="light-back-dark" className="rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em]" style={{ color: t.bg }}>
            ← Dark
          </Link>
        </div>
      </div>

      {/* Nav */}
      <header style={{ borderColor: t.border }} className="sticky top-0 z-50 border-b backdrop-blur-xl" >
        <div style={{ background: `${t.bg}cc` }} className="absolute inset-0" />
        <div className="relative mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <span style={{ borderColor: t.border, background: `${t.accent}14` }} className="grid h-8 w-8 place-items-center border">
              <span style={{ background: t.accent }} className="h-2 w-2 rotate-45" />
            </span>
            <span className="flex flex-col font-display leading-none">
              <span className="text-lg font-medium tracking-tight">VectorBridge<span style={{ color: t.accent }}>.</span></span>
              <span style={{ color: t.muted }} className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.35em]">Group</span>
            </span>
          </div>
          <nav className="hidden items-center gap-7 lg:ml-16 lg:flex xl:ml-28">
            {NAV.map((n) => (
              <span key={n} style={{ color: t.muted }} className="font-mono text-xs uppercase tracking-[0.18em]">{n}</span>
            ))}
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <span style={{ borderColor: t.border, color: t.muted }} className="border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em]">Candidate</span>
            <span style={{ background: t.accent }} className="inline-flex items-center gap-1 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white">Client Portal <ArrowUpRight size={14} /></span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ borderColor: t.border }}>
        <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-7">
            <div style={{ color: t.muted }} className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em]">
              <span style={{ color: t.accent }}>01</span>
              <span style={{ background: t.border }} className="h-px w-8" />
              National Workforce Solutions
            </div>
            <h1 className="mt-8 font-display text-[11vw] font-light leading-[0.9] tracking-tighter sm:text-7xl">
              <MaskLines lines={["The bridge between", "mission and"]} delay={0.15} />
              <span style={{ color: t.accent }}><MaskLines lines={["the right people."]} delay={0.4} /></span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              style={{ color: t.muted }} className="mt-8 max-w-xl text-lg leading-relaxed"
            >
              VectorBridge Group LLC connects government, defense, healthcare, and commercial
              organizations with talent engineered for accountability, compliance, and outcomes.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-10 flex flex-wrap gap-3">
              <span style={{ background: t.accent }} className="inline-flex items-center gap-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white">Hire Talent <ArrowUpRight size={16} /></span>
              <span style={{ borderColor: t.border }} className="inline-flex items-center gap-2 border px-6 py-4 font-mono text-xs uppercase tracking-[0.15em]">Find Work</span>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              style={{ borderColor: t.border }} className="relative aspect-[4/5] overflow-hidden border"
            >
              <img src={t.hero} alt="Architecture" className="h-full w-full object-cover" />
              <div style={{ background: t.heroTint }} className="absolute inset-0" />
              <div style={{ borderColor: t.accent }} className="absolute bottom-6 left-6 right-6 border-l-2 pl-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: t.text }}>Est. Nationwide</div>
                <div className="font-display text-2xl tracking-tight" style={{ color: t.text }}>Precision. Stability. Trust.</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div style={{ borderColor: t.border }} className="overflow-hidden border-y py-5">
        <div className="flex w-max animate-marquee whitespace-nowrap">
          {[...Array(2)].flatMap((_, r) => ["Direct Hire", "Executive Search", "Government Contracting", "RPO", "Managed Workforce", "Proposal Staffing", "Human Capital Consulting"].map((w, i) => (
            <span key={`${r}-${i}`} style={{ color: t.muted }} className="mx-8 inline-flex items-center gap-8 font-display text-2xl tracking-tight sm:text-3xl">
              {w}<span style={{ background: t.accent }} className="h-1.5 w-1.5 rotate-45" />
            </span>
          )))}
        </div>
      </div>

      {/* Stats */}
      <section style={{ borderColor: t.border }} className="border-b">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-8 px-5 py-16 sm:grid-cols-4 sm:px-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div style={{ borderColor: t.border }} className="border-l pl-5">
                <div className="font-display text-4xl font-medium tracking-tighter sm:text-5xl">{s.k}</div>
                <div style={{ color: t.muted }} className="mt-2 font-mono text-xs uppercase tracking-[0.2em]">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services */}
      <section style={{ borderColor: t.border }} className="border-b">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <div style={{ color: t.muted }} className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em]"><span style={{ color: t.accent }}>03</span><span style={{ background: t.border }} className="h-px w-8" />Capabilities</div>
              <h2 className="mt-6 font-display text-4xl font-light tracking-tighter sm:text-6xl">Twelve ways we deploy talent.</h2>
            </div>
            <span style={{ color: t.muted }} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em]">All services <ArrowRight size={16} /></span>
          </div>
          <div style={{ borderColor: t.border }} className="mt-12 grid border-l border-t sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.05}>
                <div style={{ borderColor: t.border }} className="group border-b border-r p-8 transition-colors" onMouseEnter={(e) => (e.currentTarget.style.background = t.surface)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <div className="flex items-start justify-between">
                    <span style={{ color: t.accent }} className="font-mono text-xs">{s.id}</span>
                    <ArrowUpRight size={18} style={{ color: t.muted }} />
                  </div>
                  <h3 className="mt-10 font-display text-2xl tracking-tight">{s.title}</h3>
                  <p style={{ color: t.muted }} className="mt-3 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section style={{ borderColor: t.border }} className="border-b">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          <div style={{ color: t.muted }} className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em]"><span style={{ color: t.accent }}>04</span><span style={{ background: t.border }} className="h-px w-8" />Industries Served</div>
          <div style={{ background: t.border }} className="mt-12 grid gap-px md:grid-cols-3">
            {INDUSTRIES.slice(0, 3).map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.08}>
                <div style={{ background: t.bg }} className="group relative aspect-[4/5] overflow-hidden">
                  <img src={ind.img} alt={ind.name} className="absolute inset-0 h-full w-full object-cover opacity-90 grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.05))" }} />
                  <div className="relative flex h-full flex-col justify-end p-7 text-white">
                    <div style={{ color: "#fff" }} className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-90">{ind.tag}</div>
                    <h3 className="mt-2 font-display text-2xl tracking-tight">{ind.name}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: t.surface, borderColor: t.border }} className="border-b">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-5 py-16 sm:flex-row sm:items-center sm:px-8">
          <h2 className="max-w-xl font-display text-3xl font-light tracking-tighter sm:text-4xl">Build a workforce that performs under scrutiny.</h2>
          <span style={{ background: t.accent }} className="inline-flex items-center gap-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white">Start a conversation <ArrowUpRight size={16} /></span>
        </div>
      </section>

      <div style={{ color: t.muted, borderColor: t.border }} className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-8 font-mono text-[11px] uppercase tracking-[0.15em] sm:px-8">
        <span>© {new Date().getFullYear()} VectorBridge Group LLC — {t.name} preview</span>
        <span>vectorbridgegroup.com</span>
      </div>
    </div>
  );
}
